// QAGenerationTable.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Alert } from 'react-bootstrap';
import {
  IconButton,
  TextField,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import TanstackTable from 'components/elements/advance-table/TanstackTable';
import RefreshButton from 'components/elements/refresh-button/RefreshButton';
import { getGeneratedQAsAPI, getSignedUrl, generateQnaPDF } from 'ServerAPI/serverAPI'; // Added generateQnaPDF import
import { useToast } from 'context/toast-context/ToastProvider';
import { DateDisplay } from 'helper/utils';
import { useNavigate } from 'react-router-dom';
import { Download, FileQuestion, Trash2 } from 'lucide-react';
import { useCookies } from 'react-cookie';

export const QAGenerationTable = ({ userId, setQuestionsCount }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cookies] = useCookies(['userId']);

  // FETCH LIST
  const fetchData = async (isRefresh = false) => {
    try {
      if (isRefresh) setIsRefreshing(true);
      else setLoading(true);
      setError(null);

      if (!userId) {
        showToast('User ID not found', 'error');
        return;
      }

      const resp = await getGeneratedQAsAPI(userId);
      const list = resp.data?.resources || [];

      setData(list);
      setQuestionsCount(list.length);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to load Q&A list');
      setData([]);
      setQuestionsCount(0);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (userId) fetchData();
  }, [userId]);

  const handleRefresh = () => fetchData(true);
  const handleGenerateNewQA = () => navigate('/qa-generation/');

  // DOWNLOAD - UPDATED FOR PDF GENERATION
const handleDownload = async (item) => {
  try {
    showToast('Generating PDF...', 'warning');
    
    const payload = {
      request_type: "generate_pdf",
      qnaId: item.qnaId,
      userId: cookies.userId,
      fileName: item.fileName || 'questions.pdf',
    };

    const response = await generateQnaPDF(payload);
    
    if (response.data.success && response.data.pdf_content) {
      // Convert base64 to blob and download
      const binaryString = atob(response.data.pdf_content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = item.fileName || 'questions.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      URL.revokeObjectURL(url);
      showToast('PDF downloaded successfully!', 'success');
    } else {
      throw new Error(response.data.error || 'PDF generation failed');
    }
  } catch (err) {
    console.error('Download error:', err);
    showToast(err.message || 'Download failed', 'error');
  }
};


  // DELETE - KEEP EXISTING LOGIC
  const handleDelete = async (item) => {
    if (!window.confirm('Delete this Q&A? This cannot be undone.')) return;

    try {
      showToast('Deleting...', 'warning');
      // Keep existing delete logic if needed, or remove if not applicable
      const payload = {
        qna: true,
        qnaId: item.qnaId,
        userId: cookies.userId,
        fileName: item.fileName,
      };
      await getSignedUrl('DELETE', payload);
      showToast('Deleted!', 'success');
      fetchData(); // refresh list
    } catch (err) {
      console.error(err);
      showToast('Delete failed', 'error');
    }
  };

  // SEARCH
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(
      (i) =>
        i.title?.toLowerCase().includes(term) ||
        i.status?.toLowerCase().includes(term)
    );
  }, [data, searchTerm]);

  // COLUMNS
  const columns = useMemo(
    () => [
      {
        id: 'sno',
        header: 'S.No.',
        cell: ({ row }) => row.index + 1,
        enableSorting: false,
      },
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ getValue }) => (
          <div className="fw-semibold text-truncate" style={{ maxWidth: '200px' }}>
            {getValue() || 'Untitled'}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => {
          const s = (getValue() || '').toLowerCase();
          const colors = {
            completed: 'success',
            processing: 'warning',
            failed: 'danger',
            pending: 'info',
            saved: 'primary', // Added for saved status
          };
          const label = s.charAt(0).toUpperCase() + s.slice(1);
          return <span className={`badge bg-${colors[s] || 'secondary'}`}>{label}</span>;
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created At',
        cell: ({ row }) => {
          const val = row.original.createdAt;
          let dateStr = '';
          if (typeof val === 'string') dateStr = val;
          else if (val?.$date) dateStr = val.$date;
          else if (val?.$currentDate) dateStr = new Date().toISOString();
          return (
            <span className="text-muted small">
              <DateDisplay timeString={dateStr} />
            </span>
          );
        },
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => {
          const item = row.original;
          const isCompleted = item.status?.toLowerCase() === 'completed';
          const isSaved = item.status?.toLowerCase() === 'saved';

          return (
            <div className="d-flex gap-1">
              {/* DOWNLOAD - Available for both completed and saved status */}
              {(isCompleted || isSaved) ? (
                <Tooltip title="Download PDF">
                  <IconButton size="small" onClick={() => handleDownload(item)}>
                    <Download color="#224d99" size={20} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Not ready">
                  <IconButton size="small" disabled>
                    <Download color="#ccc" size={20} />
                  </IconButton>
                </Tooltip>
              )}

              {/* DELETE */}
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => handleDelete(item)}>
                  <Trash2 color="#d32f2f" size={20} />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [cookies.userId]
  );

  // LOADING
  if (loading && !isRefreshing) {
    return (
      <div className="text-center p-5">
        <CircularProgress />
        <div className="mt-2">Loading Q&A...</div>
      </div>
    );
  }

  return (
    <>
      {/* SEARCH + BUTTONS */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search by title or status"
          InputProps={{ startAdornment: <SearchIcon /> }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '400px' }}
          className="w-100"
        />
        <div className="d-flex gap-2">
          <RefreshButton handleRefresh={handleRefresh} isRefreshing={isRefreshing} />
          <Button variant="primary" onClick={handleGenerateNewQA}>
            <AddIcon fontSize="small" /> Generate New Q&A
          </Button>
        </div>
      </div>

      {/* ERROR */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" size="sm" onClick={() => fetchData()}>
            Try Again
          </Button>
        </Alert>
      )}

      {/* TABLE OR EMPTY */}
      <div className="mt-3">
        {filteredData.length === 0 ? (
          <div className="text-center p-5">
            <FileQuestion size={48} className="text-muted" />
            <h5 className="text-muted">
              {searchTerm ? 'No matching Q&A' : 'No Q&A found'}
            </h5>
            <p className="text-muted">
              {searchTerm ? 'Try different keywords.' : 'Generate your first Q&A.'}
            </p>
            {!searchTerm && (
              <Button variant="primary" onClick={handleGenerateNewQA}>
                <AddIcon /> Generate New Q&A
              </Button>
            )}
          </div>
        ) : (
          <TanstackTable data={filteredData} columns={columns} pagination={true} />
        )}
      </div>
    </>
  );
};